import React, { useEffect, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { forwardRef } from "react";

const DynamicVariantSection = forwardRef(({
  variants,
  setSelectedVariant,
  selectedOptions,
  setSelectedOptions,
  setOutOfStock
}, ref) => {
  // Extract all option types and their unique values
  const optionTypes = useMemo(() => {
    if (!variants?.length) return [];

    const typesMap = {};

    variants.forEach((variant) => {
      const optionValues = variant?.option_values_with_hex || {};

      Object.keys(optionValues).forEach((optionTypeName) => {
        if (!typesMap[optionTypeName]) {
          typesMap[optionTypeName] = {
            name: optionTypeName,
            hasHexCode: false,
            values: new Set(),
            valuesWithHex: new Map(),
          };
        }

        const optionValue = optionValues[optionTypeName];
        // Handle both object format {value: "...", hex_code: "..."} and direct value
        const value = typeof optionValue === 'object' && optionValue !== null
          ? (optionValue.value || optionValue)
          : optionValue;
        const hexCode = typeof optionValue === 'object' && optionValue !== null
          ? optionValue.hex_code
          : null;

        if (value) {
          typesMap[optionTypeName].values.add(value);

          // Check if hex_code exists and is a string (including "000000" for black)
          if (hexCode !== null && hexCode !== undefined && typeof hexCode === 'string') {
            typesMap[optionTypeName].hasHexCode = true;
            typesMap[optionTypeName].valuesWithHex.set(value, hexCode);
          }
        }
      });
    });

    // Convert to array format
    return Object.values(typesMap)?.map((type) => ({
      ...type,
      values: Array.from(type.values),
      valuesWithHex: Object.fromEntries(type.valuesWithHex),
    }));
  }, [variants]);

  // Initialize selected options with first value of each type
  useEffect(() => {
    if (optionTypes?.length > 0 && Object?.keys(selectedOptions || {})?.length === 0) {
      const initialOptions = {};
      optionTypes?.forEach((optionType) => {
        if (optionType?.values?.length > 0) {
          initialOptions[optionType?.name] = optionType?.values?.[0];
        }
      });
      setSelectedOptions(initialOptions);
    }
    // eslint-disable-next-line
  }, [optionTypes]);

  // Find matching variant based on selected options
  useEffect(() => {
    if (Object?.keys(selectedOptions || {})?.length === optionTypes?.length && optionTypes?.length > 0) {
      const matchingVariant = variants?.find((variant) => {
        const variantOptions = variant?.option_values_with_hex || {};
        return optionTypes?.every((optionType) => {
          const selectedValue = selectedOptions?.[optionType?.name];
          const variantOptionValue = variantOptions?.[optionType?.name];
          // Handle both object format and direct value
          const variantValue = typeof variantOptionValue === 'object' && variantOptionValue !== null
            ? (variantOptionValue?.value || variantOptionValue)
            : variantOptionValue;
          return selectedValue === variantValue;
        });
      });

      if (matchingVariant) {
        setSelectedVariant?.(matchingVariant);
      } else {
        setSelectedVariant?.(null);
      }
    } else {
      setSelectedVariant?.(null);
    }
    // eslint-disable-next-line
  }, [selectedOptions, variants, optionTypes]);

  // Helper: get the string value from a variant's option entry
  const extractValue = (optionValue) => {
    if (typeof optionValue === 'object' && optionValue !== null) {
      return optionValue.value || optionValue;
    }
    return optionValue;
  };

  // Handle option change
  const handleOptionChange = (optionTypeName, newValue) => {
    const changedIndex = optionTypes.findIndex((ot) => ot.name === optionTypeName);

    // First option type (index 0) — freely selectable.
    // When it changes, auto-reset ALL downstream option types (1, 2, ...)
    // to their first valid in-stock value for the new type-0 selection.
    if (changedIndex === 0) {
      // Start with only the new type-0 value selected
      let updatedOptions = { [optionTypeName]: newValue };

      // Cascade through each remaining option type in order
      for (let i = 1; i < optionTypes.length; i++) {
        const ot = optionTypes[i];
        // Build the "already decided" options so far (type-0 + any earlier types)
        const decidedSoFar = { ...updatedOptions };

        // Find the first in-stock value for this option type that pairs with decidedSoFar
        const validValues = (ot.values || []).filter((v) =>
          variants?.some((variant) => {
            const vo = variant?.option_values_with_hex || {};
            if (extractValue(vo[ot.name]) !== v) return false;
            return (
              Object.keys(decidedSoFar).every(
                (key) => extractValue(vo[key]) === decidedSoFar[key]
              ) && (variant?.quantity || 0) > 0
            );
          })
        );

        // Pick first valid value, or fall back to the absolute first value of this type
        updatedOptions[ot.name] = validValues.length > 0 ? validValues[0] : ot.values?.[0];
      }

      setSelectedOptions(updatedOptions);
      return;
    }

    // For option types 1+:
    // Build a partial selection applying the newly chosen value
    const partialOptions = {
      ...selectedOptions,
      [optionTypeName]: newValue,
    };

    const firstOptionType = optionTypes[0];
    const currentFirstValue = selectedOptions?.[firstOptionType?.name];

    // Build the "other" options excluding option type 0 (used for validation)
    const otherPartial = { ...partialOptions };
    delete otherPartial[firstOptionType?.name];

    // Check if the CURRENTLY selected type-0 value already forms a valid
    // in-stock variant with the new selection — if so, keep it (no reset needed).
    const currentFirstStillValid = Boolean(
      currentFirstValue &&
      variants?.some((variant) => {
        const vo = variant?.option_values_with_hex || {};
        if (extractValue(vo[firstOptionType?.name]) !== currentFirstValue) return false;
        return (
          Object.keys(otherPartial).every(
            (key) => extractValue(vo[key]) === otherPartial[key]
          ) && (variant?.quantity || 0) > 0
        );
      })
    );

    let bestFirstValue = currentFirstValue; // default: keep current

    if (!currentFirstStillValid) {
      // Current type-0 value has no in-stock match → find the first valid one
      bestFirstValue = firstOptionType?.values?.[0]; // absolute fallback

      const validFirstValues = (firstOptionType?.values || []).filter((v) =>
        variants?.some((variant) => {
          const vo = variant?.option_values_with_hex || {};
          if (extractValue(vo[firstOptionType?.name]) !== v) return false;
          return (
            Object.keys(otherPartial).every(
              (key) => extractValue(vo[key]) === otherPartial[key]
            ) && (variant?.quantity || 0) > 0
          );
        })
      );

      if (validFirstValues.length > 0) {
        bestFirstValue = validFirstValues[0];
      }
    }

    // Commit: updated option type 1+ value + (possibly kept) option type 0
    let updatedOptions = {
      ...partialOptions,
      [firstOptionType?.name]: bestFirstValue,
    };

    // If the 2nd option type (index 1) changed and there are 3+ option types,
    // also auto-pick the first valid value for option type 2 (3rd).
    if (changedIndex === 1 && optionTypes.length > 2) {
      const thirdOptionType = optionTypes[2];
      const partialWithoutThird = { ...updatedOptions };
      delete partialWithoutThird[thirdOptionType.name];

      const validThirdValues = (thirdOptionType.values || []).filter((v) =>
        variants?.some((variant) => {
          const vo = variant?.option_values_with_hex || {};
          if (extractValue(vo[thirdOptionType.name]) !== v) return false;
          return (
            Object.keys(partialWithoutThird).every(
              (key) => extractValue(vo[key]) === partialWithoutThird[key]
            ) && (variant?.quantity || 0) > 0
          );
        })
      );

      if (validThirdValues.length > 0) {
        updatedOptions = {
          ...updatedOptions,
          [thirdOptionType.name]: validThirdValues[0],
        };
      }
    }

    setSelectedOptions(updatedOptions);
  };

  // Determine which values are selectable (not disabled) for each option type.
  //
  // Rules:
  //   • Option type 0 (e.g. Color):
  //       A color is available (enabled) if it has at least ONE in-stock variant,
  //       regardless of what size is currently selected.
  //       All colors with any stock remain clickable at all times.
  //
  //   • Option types 1+ (e.g. Size):
  //       A value is available if there is an in-stock variant for the
  //       CURRENTLY SELECTED type-0 value (e.g. Color=Scarlet → only M enabled).
  //       This ensures the size list reflects the chosen color.
  const getAvailableValues = (optionTypeName) => {
    const currentIndex = optionTypes.findIndex((ot) => ot.name === optionTypeName);

    // ── Option type 0: enabled if it has any in-stock variant (ignoring other selections) ──
    if (currentIndex === 0) {
      return variants
        ?.filter((variant) => (variant?.quantity || 0) > 0)
        ?.map((variant) => extractValue(variant?.option_values_with_hex?.[optionTypeName]))
        || [];
    }

    // ── Option types 1+: filter by the currently selected type-0 value ──
    const firstOptionType = optionTypes[0];
    const selectedFirstValue = selectedOptions?.[firstOptionType?.name];

    return variants
      ?.filter((variant) => {
        const vo = variant?.option_values_with_hex || {};
        // Must match the currently selected type-0 value (e.g. Color)
        if (selectedFirstValue && extractValue(vo[firstOptionType?.name]) !== selectedFirstValue) {
          return false;
        }
        return (variant?.quantity || 0) > 0;
      })
      ?.map((variant) => extractValue(variant?.option_values_with_hex?.[optionTypeName]))
      || [];
  };

  // Check if current selection is out of stock
  useEffect(() => {
    if (Object?.keys(selectedOptions || {})?.length === optionTypes?.length && optionTypes?.length > 0) {
      const matchingVariant = variants?.find((variant) => {
        const variantOptions = variant?.option_values_with_hex || {};
        return optionTypes?.every((optionType) => {
          const selectedValue = selectedOptions?.[optionType?.name];
          const variantOptionValue = variantOptions?.[optionType?.name];
          // Handle both object format and direct value
          const variantValue = typeof variantOptionValue === 'object' && variantOptionValue !== null
            ? (variantOptionValue?.value || variantOptionValue)
            : variantOptionValue;
          return selectedValue === variantValue;
        });
      });

      if (matchingVariant) {
        setOutOfStock?.((matchingVariant?.quantity || 0) <= 0);
      } else {
        setOutOfStock?.(true);
      }
    }
    // eslint-disable-next-line
  }, [selectedOptions, variants, optionTypes]);

  if (!optionTypes?.length) {
    return null;
  }

  return (
    <>
      {optionTypes?.map((optionType, index) => {
        const availableValues = getAvailableValues(optionType?.name) || [];
        const selectedValue = selectedOptions?.[optionType?.name];
        const isLastOption = index === (optionTypes?.length || 0) - 1;

        if (optionType?.hasHexCode) {
          // Render as color selector
          return (
            <Box key={optionType?.name || index} className="type-container">
              <Typography className="type">
                {optionType?.name || ''} <span className="color-value">{selectedValue || ''}</span>
              </Typography>
              <Box className="check-container">
                {optionType?.values?.map((value, i) => {
                  const hexCode = optionType?.valuesWithHex?.[value];
                  const isAvailable = availableValues?.includes(value) || false;
                  return (
                    <Box
                      key={`${optionType?.name || index}-${value}-${i}`}
                      sx={{
                        border: selectedValue === value ? "1.5px solid black" : "1.5px solid transparent",
                        padding: "2px",
                        opacity: isAvailable ? 1 : 0.5,
                      }}
                    >
                      <Box
                        className="color-check"
                        sx={{
                          backgroundColor: hexCode ? `${hexCode}` : "#ccc",
                          cursor: isAvailable ? "pointer" : "not-allowed",
                          border: "0.1px solid grey",
                        }}
                        onClick={() => isAvailable && handleOptionChange(optionType?.name, value)}
                      />
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        } else {
          // Render as text selector (like size)
          return (
            <Box
              key={optionType?.name || index}
              ref={isLastOption ? ref : null}
              className="type-container"
            >
              <Typography className="type">
                {optionType?.name || ''}: <span className="size-value">{selectedValue || ''}</span>
              </Typography>
              <Box className="size-options-container">
                {optionType?.values?.map((value, i) => {
                  const isAvailable = availableValues?.includes(value) || false;
                  return (
                    <Box
                      key={`${optionType.name}-${value}-${i}`}
                      className="size-options"
                      sx={{
                        backgroundColor: isAvailable
                          ? selectedValue === value
                            ? "#A78D78"
                            : "transparent"
                          : "#F0F0F0",
                        color: isAvailable
                          ? selectedValue === value
                            ? "white"
                            : "black"
                          : "#959595",
                        border:
                          selectedValue === value
                            ? "1.5px solid black"
                            : "1.5px solid transparent",
                        cursor: isAvailable ? "pointer" : "not-allowed",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minWidth: 0,
                        "& > *": {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        },
                      }}
                      onClick={() => isAvailable && handleOptionChange(optionType?.name, value)}
                    >
                      <Box
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          minWidth: 0,
                        }}
                        title={value}
                      >
                        {value}
                      </Box>
                      {!isAvailable && <Box className="line" />}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          );
        }
      })}
    </>
  );
});

DynamicVariantSection.displayName = "DynamicVariantSection";

export default DynamicVariantSection;

